import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { HardDrive, Files, SearchIcon } from "lucide-react";
import Sidebar from "./Sidebar";
import ImpersonationBanner from "./ImpersonationBanner";
import StorageWarningBanner from "./StorageWarningBanner";
import NotificationButton from "./NotificationButton";
import { useAppStore } from "../stores/useAppStore";
import { useUser } from "../lib/auth/useSession";
import { api } from "../services/api";
import { Badge } from "./ui/badge";

interface LayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = "stash-sidebar-collapsed";

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const user = useUser(); // Get user from React Query
  const { projects } = useAppStore();

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored === "true";
    }
    return false;
  });

  const [storageStats, setStorageStats] = useState({
    totalFiles: 0,
    totalSize: "0 B",
    formattedSize: "0",
  });

  const getFormattedSizeNumber = (sizeString: string): string =>
    sizeString.split(" ")[0];

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const newValue = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newValue));
      }
      return newValue;
    });
  };

  useEffect(() => {
    if (!user) return;

    let active = true;
    (async () => {
      try {
        // Use the storage API endpoint for accurate counts
        const usage = await api.getUserStorageUsage();

        if (!active) return;

        setStorageStats({
          totalFiles: usage.file_count,
          totalSize: usage.total_bytes_human,
          formattedSize: getFormattedSizeNumber(usage.total_bytes_human),
        });
      } catch (err) {
        console.error("Failed to fetch storage stats:", err);
      }
    })();

    return () => {
      active = false;
    };
  }, [user]);

  // User is guaranteed to exist here because AuthProvider handles auth
  if (!user) return null;

  return (
    <div className="flex h-screen overflow-hidden bg-white">
      <Sidebar
        projects={projects}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />

      <div className="flex min-w-0 flex-1 flex-col bg-white">
        <ImpersonationBanner onStopImpersonation={() => {}} />
        <StorageWarningBanner />

        <header className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur-sm sm:px-6">
          <div className="flex min-w-0 items-center space-x-4 text-sm sm:space-x-8">
            <div className="flex min-w-0 items-center space-x-2">
              <HardDrive className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="truncate font-semibold text-foreground">
                {storageStats.totalSize}
              </span>
              <span className="hidden text-muted-foreground sm:inline">
                used
              </span>
            </div>

            <div className="hidden h-4 w-px bg-border sm:block" />

            <div className="flex min-w-0 items-center space-x-2">
              <Files className="h-4 w-4 flex-shrink-0 text-green-600" />
              <span className="truncate font-semibold text-foreground">
                {storageStats.totalFiles.toLocaleString()}
              </span>
              <span className="hidden text-muted-foreground sm:inline">
                files
              </span>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center px-4">
            <button
              onClick={() =>
                window.dispatchEvent(new Event("open-global-search"))
              }
              className="group flex w-full max-w-md items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-all hover:border-primary/50 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <SearchIcon className="size-4 flex-shrink-0 transition-colors group-hover:text-primary" />
              <span className="flex-1 text-left">Search files...</span>
              <kbd className="hidden rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground sm:inline-block">
                ⌘K
              </kbd>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <NotificationButton />

            <div className="h-4 w-px bg-border" />

            <Badge
              variant="secondary"
              className="flex-shrink-0 border-green-200 bg-green-50 text-green-700"
            >
              <div className="mr-2 h-2 w-2 rounded-full bg-green-500" />
              Active
            </Badge>
            <span className="text-xs font-medium text-muted-foreground">
              v0.1.10
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-white">{children}</main>
      </div>
    </div>
  );
}
